import axios, { AxiosInstance } from 'axios';
import APPConfig from '../../config';

class APIService
{
    protected baseURL:string;
    private client: AxiosInstance;

    constructor( url:string = ''){
        //set the default api url properly
        this.baseURL = url && url.length > 0 ? url : APPConfig.API_URL;

        // Create an isolated axios instance per service to prevent interceptor accumulation
        // on the global axios object when multiple services are instantiated.
        this.client = axios.create();

        let currentConfig:any = null;
        // Add a request interceptor
        this.client.interceptors.request.use( async (config)  => {
            //get the token
            const token = localStorage.getItem('access_token');
            //get the current config so that we can call the method if there is a token expired error
            //do not get the config for refresh token call, because it means that it's refreshing the token
            if(this.baseURL + '/auth/refresh-token' !==  config.url)
                currentConfig = config;

            if(token && token.length > 0 && token != 'undefined')
                config.headers.Authorization =  'Bearer ' + token;

            window.dispatchEvent(new Event('api:loading:start'));
            return config;
        });

        //add common handlers
        this.client.interceptors.response.use((response) => {
            // do something with the response data
            window.dispatchEvent(new Event('api:loading:end'));
            return response;
          }, async (error) => {
            window.dispatchEvent(new Event('api:loading:end'));
            //handle errors here
            if (error.response) {
                if(error.response.status === 401)
                {
                    const refreshUrl = this.baseURL + '/auth/refresh-token';
                    const isRefreshCall = error.config?.url === refreshUrl;
                    const refreshToken = localStorage.getItem('refresh_token');

                    // If the failed request IS the refresh-token call, or there is no
                    // refresh token, the session can no longer be recovered (e.g. the
                    // account was deleted by an admin). Retrying would loop forever and
                    // leave the app stuck on the loading indicator — instead, clear the
                    // session and send the user back to the login page.
                    if (isRefreshCall || !refreshToken) {
                        this.forceLogout();
                        return Promise.reject(error);
                    }

                    try {
                        //if the token has expired then we have to call the refresh token and call the method again
                        const data = {access_token: localStorage.getItem('access_token'), refresh_token: refreshToken};
                        const result = await this.client.post(refreshUrl, data);

                        //set the new access token
                        localStorage.setItem('access_token', result.data.access_token);
                        //call again the previous method
                        return this.client(currentConfig);
                    } catch (refreshError) {
                        this.forceLogout();
                        return Promise.reject(refreshError);
                    }
                } else{
                    return Promise.reject(error);
                }
            } else {
                return Promise.reject(error);
            }
          });
    }

    // Clear the local session and bounce to the login page. Used when the session
    // can no longer be refreshed (expired refresh token, deleted account, ...).
    forceLogout = () => {
        window.dispatchEvent(new Event('api:loading:end'));
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('store');
        const path = window.location.pathname;
        if (path !== '/' && path !== '/login') {
            window.location.href = '/login';
        }
    }

    buildURL = (method:string):string => {
        return this.baseURL + '/' + method;
    }

    get = async (method:string) => {
        return await this.client.get(this.buildURL(method));
    }

    post = async (data:any, method:string) => {
        try
        {
            return await this.client.post(this.buildURL(method), data);
        }catch(error){

            return Promise.reject(error);
        }

    }

    put = async (data:any, method:string) => {
        return await this.client.put(this.buildURL(method), data);
    }

    remove = async (method:string) => {
        return await this.client.delete(this.buildURL(method));
    }
}

export default APIService
